<template>
  <div class="d-flex flex-column flex-sm-row" style="gap: 16px">
    <div class="d-flex flex-column flex-grow-1" style="min-width: 0px">
      <!----------------------------- Category ----------------------------->
      <div class="mb-8" v-if="
        ((categoryId == null || categoryId == '') && serviceId == null) ||
        serviceId == ''
      ">
        <div class="text-h4 py-2">{{ $translate('Choose') }} {{ categoryTitle }}</div>
        <div v-if="noCategoriesFound" class="text-caption grey--text">
          {{ $translate('None available found') }}
        </div>
        <div v-else-if="categories.length === 0" class="text-caption grey--text">
          {{ $translate('None available found') }}
        </div>
        <v-btn-toggle borderless color="primary" v-else v-model="selectedCategory" class="d-flex flex-wrap" mandatory
          variant="flat">
          <v-btn v-for="category in categories" v-bind:key="category.id" :value="category" class="flex-grow-1">
            {{ category.name }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <!----------------------------- Service ----------------------------->
      <div class="mb-8" v-if="(serviceId == null || serviceId == '') && categories.length != 0">
        <div class="text-h4 py-2">{{ $translate('Choose') }} {{ serviceTitle }}</div>
        <div v-if="filteredServices.length === 0" class="text-caption grey--text">
          {{ $translate('None available found') }}
        </div>
        <v-btn-toggle borderless mandatory color="primary" v-else v-model="selectedService" class="d-flex flex-wrap">
          <v-btn v-for="service in filteredServices" v-bind:key="service.id" :value="service" class="flex-grow-1">
            {{ service.name }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <!----------------------------- Resources ----------------------------->
      <div v-show="resourceSlots.length != 0 && !!selectedCategory && resourceStyle !== 0" class="mb-8">
        <div class="text-h4 py-2">{{ $translate('Choose resource') }}</div>
        <div v-if="availableResourceTags.length" class="mb-2">
          <v-chip
            v-for="tag in availableResourceTags"
            :key="tag.id"
            :outlined="!selectedTagIds.includes(tag.id)"
            :close="selectedTagIds.includes(tag.id)"
            color="primary"
            label
            class="mr-2 mb-2"
            @click="toggleTagFilter(tag.id)"
            @click:close="toggleTagFilter(tag.id)"
          >
            {{ tag.name }}
          </v-chip>
        </div>
        <template v-if="resourceStyle === 1">
          <v-row>
            <v-col cols="4" sm="3" v-for="resource in resources" v-bind:key="resource.resourceId"
              class="d-flex align-stretch">
              <v-card class="rounded-lg flex-grow-1" gap="0">
                <div @click="toggleResource(resource.resourceId)" style="cursor: pointer" class="white">
                  <v-auto-img class="zoezi-staff-image rounded-lg pa-2 d-flex align-center" cover :src="resource.imagekey"
                    style="position: relative; aspect-ratio: 16/10">
                    <v-fade-transition>
                      <v-overlay v-show="selectedResourceIds.includes(resource.resourceId)" absolute color="white"
                        :style="{ zIndex: 1 }" />
                    </v-fade-transition>
                    <v-btn dark fab x-small :color="selectedResourceIds.includes(resource.resourceId)
                      ? 'primary'
                      : 'grey'
                      " :outlined="!selectedResourceIds.includes(resource.resourceId)
                        " class="top-right-btn" :style="{ zIndex: 2 }">
                      <v-icon v-show="selectedResourceIds.includes(resource.resourceId)" dark small>mdi-check</v-icon>
                    </v-btn>
                    <v-icon v-if="resource.imagekey == null || resource.imagekey == ''"
                      class="d-flex justify-center">mdi-image-off</v-icon>
                  </v-auto-img>
                </div>
                <v-card-title class="text-subtitle-1 font-weight-medium pt-2 pb-0 text-break resource-card-title">
                  {{ resource.title }}
                </v-card-title>
                <v-card-actions class="pt-0">
                  <v-btn text color="primary" @click="openDialog(resource.resourceId)">{{ $translate('Read more')
                  }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </template>
        <template v-else-if="resourceStyle === 2">
          <div class="z-compact-resources d-flex flex-wrap">
            <v-chip
              v-for="resource in resources"
              :key="resource.resourceId"
              class="ma-1 z-compact-chip"
              label
              :outlined="!selectedResourceIds.includes(resource.resourceId)"
              :color="selectedResourceIds.includes(resource.resourceId) ? 'primary' : undefined"
              @click="toggleResource(resource.resourceId)"
            >
              <v-avatar left size="24">
                <v-auto-img :src="resource.imagekey">
                  <v-icon v-if="!resource.imagekey" small>mdi-image-off</v-icon>
                </v-auto-img>
              </v-avatar>
              <span class="z-compact-name text-truncate">{{ resource.title }}</span>
              <v-btn icon x-small class="ml-1" @click.stop="openDialog(resource.resourceId)">
                <v-icon small>mdi-information-outline</v-icon>
              </v-btn>
            </v-chip>
          </div>
        </template>
      </div>
      <!----------------------------- Time ----------------------------->
      <div ref="top" class="mb-8">
        <div class="text-h4 py-2">{{ $translate('When do you want to book?') }}</div>
        <div class="d-flex align-center">
          <label class="mr-2"><b>{{ $translate('Day') }}</b></label>
          <v-btn icon small color="primary" @click="decrementDate" class="mr-2" :disabled="isDateToday">
            <v-icon dark small>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn @click="toggleDatePicker" ref="datepickerButton" text py-1 color="primary" class="mr-2">
            {{ selectedDate }}
            <label v-show="selectedDate === Date.today().yyyymmdd()">
              ({{ $translate('Today') }})</label>
          </v-btn>
          <v-btn icon small color="primary" @click="incrementDate">
            <v-icon dark small>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
        <v-card v-show="showDatePicker" style="position: absolute; z-index: 2; padding-top: 8px" max-width="300">
          <DatePicker
            :value="selectedDate"
            :min-date="$store.state.now30s.clone().clearTime().yyyymmdd()"
            id="datepicker"
            ref="datepicker"
            @input="selectedDate = $event.yyyymmdd()"
          />

          <div class="mx-2 mt-4">
            <filter-button-row icon="mdi-calendar-today" :name="$translate('All day')" :value="[0, DAY_PERIODS.length].includes(selectedDayPeriods.length)
              " />
            <filter-button-row v-for="item in DAY_PERIODS" :key="item.id" :color="item.color"
              :name="$translate(item.title)" :icon="item.icon" :value="selectedDayPeriods.includes(item.id)"
              @input="setItem(selectedDayPeriods, item.id, $event)" />
          </div>
        </v-card>
        <v-divider />

        <div v-if="filteredResources.length == 0 || filteredSlots.length == 0"
          class="ma-6 d-flex align-center justify-center">
          <v-icon class="mr-2">mdi-calendar-alert-outline</v-icon>{{ $translate('No available times found') }}
        </div>
        <div v-else class="d-flex flex-column flex-sm-row slots-container">
          <div class="d-flex flex-row flex-sm-column mt-0 mt-sm-9 flex-grow-0">
            <div v-for="resource in filteredResources" v-bind:key="resource.resourceId"
              class="d-flex align-center flex-column flex-sm-row resource-slot mt-2 mt-sm-0 mb-2 mr-2">
              <v-auto-img
                class="zoezi-staff-image elevation-0 rounded-lg mr-sm-4 d-flex align-center white flex-grow-0 resource-image-small"
                cover aspect-ratio="16/10" :src="resource.imagekey" style="position: relative">
                <v-icon v-if="resource.imagekey == null || resource.imagekey == ''"
                  class="d-flex justify-center">mdi-image-off</v-icon>
              </v-auto-img>
              <div class="d-flex justify-space-between flex-grow-1" style="height: 52px">
                <div class="text-subtitle-1 font-weight-medium text-wrap flex-grow-0 overflow-hidden resource-title">
                  {{ resource.title }}
                </div>
                <v-btn icon small color="primary" @click="openDialog(resource.id)" class="">
                  <v-icon dark small>mdi-information-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
          <div class="slot-grid d-flex flex-row flex-sm-column align-start justify-start" style="width: 100%">
            <div v-if="Array.from(uniqueStarts).length > 0 && !isMobile"
              class="slot-row flex-shrink-1 d-flex flex-column flex-sm-row">
              <div class="timeslots select-all-slots">
                <div v-for="start in Array.from(uniqueStarts).sort()" :key="start" style="width: 100px"
                  class="d-flex align-center justify-center">
                  <v-btn
                    text
                    elevation="0"
                    plain
                    small
                    :disabled="isReserving"
                    @click="handleColumnClick(start)"
                  >
                    {{ getColumnButtonText(start) }}
                    <v-icon small>{{ getColumnButtonIcon(start) }}</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-for="resource in filteredResources" v-bind:key="resource.resourceId"
              class="slot-row d-flex flex-column flex-sm-row">
              <div v-for="start in Array.from(uniqueStarts).sort()" :key="start" class="timeslot" :loading="reserving">
                <v-btn :key="start" x-large block v-if="hasSlotForStart(resource, start)"
                  @click="handleSlotClick(resource, start)" :color="getSlotColor(resource, start)"
                  :disabled="isReserving" depressed>
                  {{ Date.newFull(start).hhmm() }}
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!----------------------------- Resourcedialog ----------------------------->
      <StaffInfoDialog :value="isDialogShown" :staffId="dialogStaffId" @input="isDialogShown = $event"/>
      <!----------------------------- Checkout  ----------------------------->
      <bottom-dialog v-model="showCheckout" scrollable max-width="850px" :dialog="!$vuetify.breakpoint.mobile"
        :persistent="!almostdone" v-if="showCheckout">
        <v-card class="zoezi-bottom-sheet" tile elevation="0">
          <v-card-title class="headline align-start flex-column pa-2 primary primarytext--text">
            <div class="font-weight-bold fill-width">
              <span v-if="!almostdone">{{
                $translate("Complete booking")
              }}</span>
              <v-btn icon @click="showCheckout = false" class="float-right" v-if="!checkoutLoading"><v-icon
                  class="primary primarytext--text">mdi-close</v-icon></v-btn>
            </div>
          </v-card-title>

          <v-card-text :class="{
            'px-2': !almostdone && $vuetify.breakpoint.mobile,
            'px-0': almostdone,
            'pb-0': almostdone,
            primary: almostdone,
          }" class="z-no-x-scroll" style="min-height: 50vh">
            <checkout ref="checkout" class="overflow-y-auto" :items="checkoutItems" expand-first-item
              :show-summary="true" :show-back-link="false" :show-payment-button="false" :show-sum="showPrice"
              :force-done="almostdone" @done="checkoutDone" @almostdone="almostdone = true"
              @loading="checkoutLoading = $event" @cost="cost = $event" @readyToPay="readyToPay = $event"
              @readyToPayExceptPaymentMethod="
                readyToPayExceptPaymentMethod = $event
                " @changeNumberOfItems="changeNumberOfItems" content-target="checkoutcontent" :dialog="true"
              :new-style-done-dialog="true" @reservation-timeout="reservationTimeout">
              <template #done="{ context: selectedSlot }">
                <v-sheet class="zoezi-resourcebooking-done" color="primary">
                  <div class="zoezi-resourcebooking-done-header-row mb-4 d-flex justify-center primarytext--text">
                    <i class="done-icon" :class="{
                      active: animateDone,
                    }" style="font-size: 52px"></i>
                  </div>

                  <v-row class="zoezi-resourcebooking-done-resource-row" no-gutters>
                    <v-col align-self="end" style="max-width: 80px">
                      <v-avatar size="60" class="mx-2">
                        <v-auto-img class="zoezi-staff-image" :src="selectedSlot.staff.imagekey" />
                      </v-avatar>
                    </v-col>
                    <v-col style="max-width: 350px">
                      <v-card class="ma-2 pointer-lb">
                        <v-card-text class="pa-3">
                          <div class="zoezi-resourcebooking-done-welcome primary--text font-weight-bold">
                            {{ $translate("Warm welcome!") }}
                          </div>
                          <div class="font-weight-bold">
                            {{ selectedSlot.service.name }}
                          </div>
                          <div>
                            <v-icon small class="mr-1">mdi-calendar-month-outline</v-icon>{{
                              formatTime(
                                selectedSlot.start,
                                selectedSlot.period
                              )
                            }}
                          </div>
                          <div>
                            <v-icon small class="mr-1">mdi-clock-outline</v-icon>{{
                              $translate(
                                "At {0} o'clock".format(
                                  selectedSlot.start.hhmm()
                                )
                              )
                            }}
                          </div>
                          <div class="my-2">
                            &ndash;{{ selectedSlot.staff.name }}
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <v-row class="zoezi-resourcebooking-done-location-row mt-2" no-gutters>
                    <v-col class="ml-auto" style="max-width: 350px">
                      <v-card class="ma-2 pointer-rb">
                        <v-card-text class="pa-3">
                          <div>
                            <div>
                              {{ contactinfo.visit_address }}
                            </div>
                            <div>
                              {{ contactinfo.visit_zipCode }}
                              {{ contactinfo.visit_city }}
                            </div>
                          </div>
                          <v-btn color="primary" block class="mt-2" v-on:click="openGoogle()">{{ $translate("Show on Google") }}
                            <v-icon class="ml-2">mdi-export</v-icon></v-btn>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col align-self="end" style="max-width: 50px">
                      <v-icon x-large class="mb-4" color="primarytext">mdi-map-marker-outline</v-icon>
                    </v-col>
                  </v-row>

                  <v-btn v-if="numBookings" @click="gotoMyPages" color="primary" fab bottom right fixed :style="{
                    bottom: $vuetify.breakpoint.mobile
                      ? 'calc(56px + 16px)'
                      : '16px',
                  }">
                    <v-badge :key="numBookings" :content="numBookings" color="red" :value="numBookings"
                      class="animate__animated animate__heartBeat" left>
                      <v-icon color="primarytext">mdi-ticket</v-icon>
                    </v-badge>
                  </v-btn>
                </v-sheet>
              </template>

              <template #content>
                <stepper-content v-model="currentStep" :steps="steps">
                  <template #questions>
                    <v-form @submit.prevent="
                      questionsDone && $refs.stepper.nextClicked()
                      " v-model="questionsDone">
                      <v-expansion-panels v-model="expandedQuestionPanel" accordion flat>
                        <v-expansion-panel
                          v-for="(reservation, index) in reservationsWithQuestions"
                          :key="index"
                        >
                          <expansion-panel-header
                            icon="mdi-comment-question-outline"
                            :title="getQuestionPanelTitle(reservation)"
                            :subtitle="$translate('Answer the questions to proceed')"
                          />
                          <v-expansion-panel-content class="z-dense">                  
                            <form-field
                              v-for="q in reservation.questions"
                              :key="q.name"
                              :field="q"
                              star
                              v-model="q.value"
                            />
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-form>
                  </template>

                  <template #optional_resources>
                    <OptionalResources class="OptionalResources" :disabled="isReserving"
                      :resource-name="steps[currentStep].context.resource_name"
                      :resource-ids="steps[currentStep].context.resource_ids"
                      :start-time="steps[currentStep].context.start" :end-time="steps[currentStep].context.end"
                      :all-resources="steps[currentStep].context.service.resources" 
                      :selected-resource-ids="steps[currentStep].context.selected_resource_ids" 
                      :bookings="optionalCtx.groupBookings"
                      @select="
                          (resource_id, selected) =>
                            optionalResourceSelectedGroup(
                              steps[currentStep].context.reservation,
                              steps[currentStep].context.groupBookings,
                              resource_id,
                              selected
                            )
                        " />
                  </template>

                  <template #extra_person>
                    <div class="mb-2 font-weight-bold">
                      {{
                        $translate(
                          "You have the opportunity to schedule more at the same time and resource. Do you want to do that? "
                      )
                      }}
                    </div>

                    <div>
                      <v-icon>mdi-account-question-outline</v-icon><span class="ml-2">{{
                        $translate("Who to book?")
                      }}</span>
                    </div>

                    <div class="z-rounded-box my-2">
                      <div class="pa-2 d-flex">
                        <div class="ml-2 flex-grow-1">
                          <span class="font-weight-bold" v-if="extraPersons.length == 0">{{
                            $store.state.user && $store.state.user.name
                            }}
                            ({{ $translate("You") }})</span>
                          <span class="font-weight-bold" v-else>{{
                            $translate("{0} people including you").format(
                              extraPersons.length + 1
                            )
                          }}</span>
                        </div>
                      </div>

                      <div class="px-2 py-1 d-flex align-center" v-for="(person, index) in extraPersons" :key="index">
                        <v-text-field v-model="person.name" :label="$translate('Name')" dense :rules="[
                          (v) =>
                            !!v ||
                            $translate('Write the name of the participant'),
                        ]" />
                        <v-btn class="ml-3" @click="removeExtraPerson(index)" icon>
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <div v-if="checkingAvailability" class="d-flex align-center">
                      <v-progress-circular indeterminate size="24"></v-progress-circular>
                      <span class="ml-2">{{ $translate("Updating...") }}</span>
                    </div>
                    <div>
                      {{ seatsLeftText }}
                    </div>

                    <v-btn color="primary" :outlined="true" block class="zoezi-button-thin" @click="addExtraPerson"
                      :disabled="!seatsLeft || isReserving || checkingAvailability
                        ">
                      <v-icon left color="primary">mdi-account-plus</v-icon>
                      {{ $translate("Add participant") }}
                    </v-btn>
                  </template>

                  <template #products>
                    <v-expansion-panels v-model="expandedProductPanel" accordion flat>
                      <v-expansion-panel class="mt-2" v-for="reservation in reservations.filter(
                        (r) => additionalProducts[r.key].length
                      )" :key="reservation.key">
                        <expansion-panel-header icon="mdi-tag-plus-outline" :title="$translate('Buy more for {0}').format(
                          services.find(
                            (s) =>
                              s.id == reservation.bookings[0].rbservice_id
                          ).name
                        )
                          " :subtitle="$translate('Buy the products you need')" />
                        <v-expansion-panel-content class="z-dense">
                          <additional-product-list :products="additionalProducts[reservation.key]"
                            :basket="reservation.basket" :total-basket="productsInBasket" :show-added-banner="false" />
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </template>
                  <template #checkout>
                    <portal-target name="checkoutcontent" />
                  </template>
                </stepper-content>
              </template>
            </checkout>
          </v-card-text>
          <v-card-actions v-if="!almostdone">
            <stepper v-model="currentStep" :steps="steps" :fixed="$vuetify.breakpoint.mobile"
              @cancel="showCheckout = false" ref="stepper" :show="!almostdone" :disabled="checkoutLoading" />
          </v-card-actions>
        </v-card>
      </bottom-dialog>
    </div>

    <!----------------------------- Summary for reservation ----------------------------->
    <div class="d-flex flex-column flex-shrink-0 w-sm-100 checkout-summary">
      <div class="d-flex flex-column flex-grow-0 flex-shrink-0 checkout-card sticky-column">
        <v-card class="mb-4">
          <v-card-title> {{ $translate('Your Booking') }} </v-card-title>
          <v-divider class="mx-4"></v-divider>
          <v-card-title class="pt-4 pb-0">{{
            capitalizeFirstLetter(categoryTitle)
          }}</v-card-title>
          <v-card-text v-if="
            !selectedCategory &&
            globalSiteSetting &&
            !this.$store.state.selectedSiteId
          " class="text-caption grey--text pt-0">
            {{ $translate('Choose site') }}
          </v-card-text>
          <v-card-text v-else-if="!selectedCategory" class="text-caption grey--text pt-0">
            {{ $translate('NO_PRODUCT_CHOSEN') }}
          </v-card-text>
          <v-card-text v-else class="pt-0">{{
            selectedCategory ? selectedCategory.name : ""
          }}</v-card-text>
          <v-card-title class="pt-4 pb-0">{{ $translate('Reserved times') }}</v-card-title>
          <v-card-text>
            <div v-if="formattedShoppingCart.length === 0" class="text-caption grey--text">
              {{ $translate('NO_PRODUCT_CHOSEN') }}
            </div>
            <div v-for="(dayEntry, dateIndex) in formattedShoppingCart" :key="dateIndex"
              class="d-flex flex-column mb-3">
              <span class="text-subtitle-2"><v-icon small class="mr-2">mdi-calendar</v-icon>
                {{ Date.formatFullShortRelative(dayEntry.date) }}</span>
              <div v-for="(timeslot, timeIndex) in dayEntry.timeslots" :key="timeIndex" class="d-flex flex-column mb-1">
                <span class="d-flex align-center text-body-2 font-weight-bold ml-0"><v-icon small
                    class="mr-2">mdi-clock-outline</v-icon>
                  {{ formatTimeOnly(timeslot.start) }} -
                  {{ formatTimeOnly(timeslot.end) }}</span>
                <div v-for="(staffId, staffIdx) in timeslot.staffIds" :key="staffIdx"
                  class="d-flex flex-row justify-space-between align-center pl-7 text-body-2" style="min-height: 24px">
                  <span class="text-break">{{ storedStaffNames[staffId] }}</span>
                  <v-btn icon x-small @click="
                    removeBooking(
                      dayEntry.date,
                      timeslot.start,
                      timeslot.end,
                      staffId
                    )
                    " :disabled="isReserving">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-text class="pt-0">
            <i><v-icon dense>mdi-pin-outline</v-icon>{{ $translate('MYBUSINESS_RESOURCE_RESERVATION_INFO') }}.</i>
          </v-card-text>
        </v-card>
        <v-btn large color="primary" @click="showCheckout = true" :disabled="formattedShoppingCart.length == 0">
          {{ $translate('Go forward for payment') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import StaffInfoDialog from "../StaffInfoDialog.vue";
import FilterButtonRow from "../FilterButtonRow.vue";
import DatePicker from "../DatePicker.vue";
import * as Sentry from "@sentry/browser";
import '../../dateextensions.js';

import { getName, mergeBaskets, getRandomId, formatPrice } from "../../util.js";
import { DAY_PERIODS } from "../../util.js";
import ExpansionPanelHeader from "../../views/ExpansionPanelHeader.vue";
import AdditionalProductList from "../../views/AdditionalProductList.vue";

import BottomDialog from "../BottomDialog.vue";
import Checkout from "../../views/Checkout.vue";
import Stepper from "../../views/Stepper.vue";
import StepperContent from "../../views/StepperContent.vue";
import OptionalResources from "../../views/OptionalResources.vue";

export default {
  name: "zoezi-resourcebookingnew",
  components: {
    StaffInfoDialog,
    FilterButtonRow,
    DatePicker,
    StepperContent,
    AdditionalProductList,
    ExpansionPanelHeader,
    Stepper,
    BottomDialog,
    Checkout,
    OptionalResources,
  },
  zoezi: {
    title: "Resource Booking Flow",
    icon: "mdi-account-box-outline",
  },
  data: () => ({
    selectedDate: Date.today().yyyymmdd(),
    showDatePicker: false,
    selectedDayPeriods: [],
    almostdone: false,

    services: [],
    resourceSlots: [],
    selectedCategory: null,
    selectedService: null,
    selectedResourceIds: [],
    selectedSlotIds: [],
    selectedSlotColumns: [],
    selectedTagIds: [],
    uniqueStarts: new Set(),
    reservations: [],
    extraPersons: [],
    dayPeriods: DAY_PERIODS,

    showCheckout: false,
    checkoutLoading: false,
    currentStep: 0,
    cost: null,
    readyToPay: false,
    readyToPayExceptPaymentMethod: false,
    reserving: false,
    expandedQuestionPanel: 0,
    questionsDone: false,
    storedStaffNames: {},
    
    isDialogShown: false,
    dialogStaffId: null,
    infoResourceTitle: null,
    infoResourceDescription: null,
    infoResourceImageKey: null,
    bookingId: getRandomId(8),

    products: [],
    expandedProductPanel: 0,
    showBookTimesSheet: false,
    unsubscribe: null,
    animateDone: false,
    reloading: false,
    requestIndex: 1,
    throttling: null,
    seatsLeft: null,
    checkingAvailability: false,
    selectedSlotHasCapacity: null,

    bulkSelecting: false,
    suppressRealtime: false,

    isMobile: false,
  }),
  props: {
    categoryId: {
      title: "Category",
      type: Number,
      fulltype: "select",
      typeinfo: {
        multiple: false,
        text: "name",
        value: "id",
        filter: (category, props) => {
          const hasServices =
            Array.isArray(category.services) && category.services.length > 0;

          if (!props.globalSiteSetting) {
            return hasServices;
          }

          return category.sites.includes(window.$store.state.selectedSiteId);
        },
        getItems: async () => {
          return await window.$booking.getResourceBookingCategories();
        },
        selectFirst: false,
      },
    },
    categoryTitle: {
      title: "Category title",
      type: String,
      default: "Category",
    },
    serviceId: {
      title: "Service",
      type: [Number, Array],
      fulltype: "select",
      typeinfo: {
        multiple: false,
        text: "name",
        value: "id",
        filter: (x, props) => {
          const matchesCategory = props.categoryId
            ? x.category_id == props.categoryId
            : true;
          const isShown = x.show;

          if (!props.globalSiteSetting || !props.selectedSiteId) {
            return matchesCategory && isShown;
          }

          const matchesSite =
            (x.site_id && x.site_id === props.selectedSiteId) ||
            (Array.isArray(x.sites) && x.sites.includes(props.selectedSiteId));

          return matchesCategory && isShown && matchesSite;
        },
        getItems: () =>
          window.$zoeziapi.get("/api/resourcebooking/service/get/all"),
      },
      default: () => [],
    },
    serviceTitle: {
      title: "Service title",
      type: String,
      default: "Service",
    },
    resourceStyle: {
        title: 'Appearance',
        type: Number,
        fulltype: 'select',
        typeinfo: {
            text: 'name',
            value: 'id',
            clearable: false,
            placeholder: 'Standard',
            getItems: () => ([
                {id: 0, name: 'Hide resource'},
                {id: 1, name: 'Large cards'},
                {id: 2, name: 'Compact cards'}
            ].map(x => ({...x, name: window.$translate(x.name)})))
        },
        default: () => 1
    },
    globalSiteSetting: {
      title: "Filter by globally selected site",
      type: Boolean,
      default: false,
    },
  },

  watch: {
    "$store.state.selectedSiteId": {
      async handler() {
        this.selectedCategory = null;
        await this.reset();
        await this.getServices();
        await this.getFreeSlots();
      },
      immediate: true,
    },
    categoryId: {
      immediate: true,
      async handler(newCategoryId) {
        if (newCategoryId !== null && newCategoryId !== "") {
          if (this.categories.length === 0) {
            if (
              !this.$store.state?.settings?.resourcebookingcategories?.length
            ) {
              await this.$store.dispatch("fetchResourceBookingCategories");
            }
          }
          const foundCategory = this.categories.find(
            (c) => c.id === newCategoryId
          );
          if (foundCategory) {
            this.selectedCategory = foundCategory;
            await this.getServices(foundCategory.id);
          }
        } else {
          this.selectedCategory = null;
        }
      },
    },
    serviceId: {
      immediate: true,
      async handler(newServiceId) {
        if (newServiceId !== null && newServiceId !== "") {
          if (this.selectedCategory && this.services.length === 0) {
            await this.getServices(this.selectedCategory.id);
          } else if (!this.selectedCategory && this.categoryId) {
            const foundCategory = this.categories.find(
              (c) => c.id === this.categoryId
            );
            if (foundCategory) {
              this.selectedCategory = foundCategory;
              await this.getServices(foundCategory.id);
            }
          }

          const foundService = this.services.find((s) => s.id === newServiceId);
          if (foundService) {
            this.selectedService = foundService;
            if (
              !this.selectedCategory ||
              this.selectedCategory.id !== foundService.category_id
            ) {
              this.selectedCategory = this.categories.find(
                (c) => c.id === foundService.category_id
              );
            }
            this.resetSelections();
            await this.getFreeSlots();
          } else {
            this.selectedService = null;
            this.resetSelections();
          }
        } else {
          this.selectedService = null;
          this.resetSelections();
        }
      },
    },
    isDialogShown(newValue) {
      if (newValue !== this.isDialogShown) {
        this.$emit("input", newValue);
      }
    },
    async selectedDate() {
      this.$vuetify.goTo(this.$refs.top);
      await this.getFreeSlots();
    },
    async selectedCategory(category, oldCategory) {
      if (category && category.id !== (oldCategory ? oldCategory.id : null)) {
        if (
          !this.selectedService ||
          this.selectedService.category_id !== category.id
        ) {
          this.selectedService = null;
        }
        this.unreserveAll();
        this.resetSelections();
        await this.getServices(category.id);
      } else if (category === null && oldCategory !== null) {
        this.services = [];
        this.selectedService = null;
        this.unreserveAll();
        this.resetSelections();
      }
    },
    async selectedService(newValue, oldValue) {
      if (newValue && (!oldValue || newValue.id !== oldValue.id)) {
        this.resetSelections();
        await this.getFreeSlots();
      } else if (newValue === null && oldValue !== null) {
        this.resetSelections();
        this.resourceSlots = {};
      }
    },
    reservations() {
      if (this.reservations && this.reservations.length) {
          if (this.category && !this.category.multiBooking) {
              this.currentStep = 0;
              this.showCheckout = true;
          } else {
              if (this.category && this.category.multiBooking) {
                  this.showBookTimesSheet = true;
              }
          }
      } else {
          this.showBookTimesSheet = false;
      }

      this.reservations.forEach(r => {
          let service = this.services.find(s => s.id == r.bookings[0].rbservice_id);
          if (!r.questions) {
              let questions = {};
              service.extendedInfo.forEach(q => {
                  q = Object.assign({}, q, {value: '', title: q.name});
                  questions[q.name] = q;
              })
              this.$set(r, 'questions', questions);
          }

          if (!r.basket) {
              this.$set(r, 'basket', {})
          }

          if (service.additional_products) {
              service.additional_products.forEach(id => this.$set(r.basket, id, []));
              let ids = String(service.additional_products.filter(id => !this.products.find(p => p.id == id)));
              if (ids) {
                  this.$api.get('/api/public/trainingcard/type/get', {ids}).then(products => {
                      products.forEach(p => {
                          if (!this.products.find(x => x.id == p.id)) {
                              this.products.push(p);
                          }
                      })
                  });
              }
          }
      })
    },
    resources: {
      immediate: true,
      handler(newResources) {
        for (const resource of newResources) {
          this.storedStaffNames[resource.resourceId] = resource.title;
        }
      },
    },
  },

  computed: {
    optionalCtx() {
      const steps = this.steps;
      const i = this.currentStep;

      const empty = {
        resource_name: "",
        resource_ids: [],
        start: null,
        end: null,
        service: { resources: [] },
        reservation: null,
        groupBookings: [],
        selected_resource_ids: [],
      };

      if (!Array.isArray(steps) || i < 0 || i >= steps.length) return empty;
      const c = steps[i] && steps[i].context ? steps[i].context : null;
      if (!c) return empty;

      return {
        resource_name: c.resource_name || "",
        resource_ids: Array.isArray(c.resource_ids) ? c.resource_ids : [],
        start: c.start || null,
        end: c.end || null,
        service: c.service || { resources: [] },
        reservation: c.reservation || null,
        groupBookings: Array.isArray(c.groupBookings) ? c.groupBookings : [],
        selected_resource_ids: Array.isArray(c.selected_resource_ids) ? c.selected_resource_ids : [],
      };
    },
    selectedSlotKeys() {
      const s = new Set();
      for (const r of this.reservations) {
        for (const b of r.bookings) s.add(`${b.start}|${b.staff_id}`);
      }
      return s;
    },
    formattedShoppingCart() {
      const cart = {};

      this.reservations.forEach((reservation) => {
        reservation.bookings.forEach((booking) => {
          const bookingDate = booking.start.split(" ")[0];
          const startTime = booking.start.split(" ")[1];
          const endTime = booking.end.split(" ")[1];
          const staffId = booking.staff_id;

          if (!cart[bookingDate]) {
            cart[bookingDate] = {
              date: bookingDate,
              timeslots: [],
            };
          }

          let existingTimeslot = cart[bookingDate].timeslots.find(
            (ts) => ts.start === startTime && ts.end === endTime
          );

          if (existingTimeslot) {
            if (!existingTimeslot.staffIds.includes(staffId)) {
              existingTimeslot.staffIds.push(staffId);
            }
          } else {
            cart[bookingDate].timeslots.push({
              start: startTime,
              end: endTime,
              staffIds: [staffId],
            });
          }
        });
      });

      const sortedCart = Object.values(cart).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      sortedCart.forEach((dayEntry) => {
        dayEntry.timeslots.sort((a, b) => {
          const timeA = new Date(`2000/01/01 ${a.start}`);
          const timeB = new Date(`2000/01/01 ${b.start}`);
          return timeA - timeB;
        });
      });

      return sortedCart;
    },
    noCategoriesFound() {
      return (
        this.categories.length === 0 &&
        this.globalSiteSetting &&
        !this.$store.state.selectedSiteId
      );
    },
    seatsLeftText() {
      if (this.seatsLeft == 1) {
        return this.$translate("There is currently 1 spot left");
      }
      return this.$translate("There are currently {0} seats left").format(this.seatsLeft);
    },
    reservationsWithQuestions() {
      return this.reservations.filter(
        (r) => r.questions && Object.keys(r.questions).length
      );
    },
    DAY_PERIODS() {
      return DAY_PERIODS;
    },
    isDateToday() {
      return this.selectedDate === Date.today().yyyymmdd();
    },
    categories() {
      if (this.globalSiteSetting && !this.$store.state.selectedSiteId) {
        return [];
      }
      let categories = [];
      for (let cat of this.$store.state?.settings?.resourcebookingcategories ||
        []) {
        if (cat.services && cat.services.length !== 0) {
          if (
            this.globalSiteSetting &&
            this.$store.state.selectedSiteId &&
            !cat.sites.includes(this.$store.state.selectedSiteId)
          )
            continue;
          categories.push(cat);
        }
      }
      return categories;
    },

    allResources() {
      return Object.entries(this.resourceSlots)
        .filter(
          ([id]) =>
            this.selectedService &&
            this.selectedService.staffs.map(s => s.id).includes(Number(id))
        )
        .map(([id, resource]) => ({ id: Number(id), ...resource }))
        .sort((a, b) => a.title.localeCompare(b.title));
    },

    resources() {
      if (!this.selectedTagIds.length) return this.allResources;
      return this.allResources.filter(r =>
        (r.tags || []).some(t => this.selectedTagIds.includes(t.id))
      );
    },

    selectedResources() {
      if (this.selectedResourceIds.length === 0) {
        return this.resources;
      }
      return this.resources.filter(resource =>
        this.selectedResourceIds.includes(resource.resourceId)
      );
    },
    filteredServices() {
      if (!this.selectedCategory) {
        return [];
      }
      return this.services.filter(
        (service) => service.category_id === this.selectedCategory.id
      );
    },
    availableResourceTags() {
      const tags = [];
      for (const r of this.allResources) {
        (r.tags || []).forEach(t => {
          if (!tags.find(x => x.id === t.id)) tags.push(t);
        });
      }
      return tags.sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredResources() {
      const passTagFilter = (resource) => {
        if (!this.selectedTagIds.length) return true;
        const rt = resource.tags || [];
        return rt.some(t => this.selectedTagIds.includes(t.id));
      };

      return this.selectedResources
        .filter(resource => passTagFilter(resource))
        .filter(resource => resource.slots.length !== 0);
    },
    slots() {
      let sortedResults = [];
      let uniqueStarts = new Set();

      for (let resource of this.selectedResources) {
        for (let freeSlot of resource.slots) {
          if (!uniqueStarts.has(freeSlot.start)) {
            uniqueStarts.add(freeSlot.start);
          }

          sortedResults.push({
            start: freeSlot.start,
            end: freeSlot.end,
            period: this.getPeriod(freeSlot.start),
            resourceId: resource.resourceId,
            sites: resource.sites,
            serviceId: resource.serviceId,
          });
        }
      }

      this.$nextTick(() => {
        this.uniqueStarts = uniqueStarts;
      });

      sortedResults.sort((a, b) => new Date(a.start) - new Date(b.start));
      return sortedResults;
    },
    filteredSlots() {
      if (this.selectedDayPeriods.length === 0) {
        return this.slots;
      } else {
        return this.slots.filter((slot) =>
          this.selectedDayPeriods.includes(slot.period.id)
        );
      }
    },

    isColumnSelected() {
      return (slotStart) => {
        const activeResources = this.filteredResources;

        if (activeResources.length === 0) {
          return false;
        }

        const allResourcesForThisTimeAreSelected = activeResources.every(
          (resource) => {
            const hasSlot = resource.slots.some((s) => s.start === slotStart);
            if (!hasSlot) {
              return true;
            }
            return this.isSlotSelected(slotStart, resource.resourceId);
          }
        );

        return allResourcesForThisTimeAreSelected;
      };
    },
    staffs() {
      let staffs = [];
      this.services.forEach((s) => {
        s.staffs.forEach((staff) => {
          staff.name = getName(staff);
          if (!staffs.find((s) => s.id == staff.id)) {
            staffs.push({
              id: staff.id,
              name: staff.name,
              imagekey: staff.imagekey,
              sites: staff.sites,
            });
          }
        });
      });
      return staffs;
    },
    additionalProducts() {
      let res = {};
      this.reservations.forEach((r) => {
        let service = this.services.find(
          (s) => s.id == r.bookings[0].rbservice_id
        );
        res[r.key] = (service.additional_products || [])
          .map((product_id) =>
            this.products.find((product) => product.id == product_id)
          )
          .filter((x) => x);
      });

      return res;
    },
    checkoutItems() {
      if (this.reservations.length == 0) {
        return [];
      }

      let items = this.reservations.map((r, index) => ({
        count: 1 + this.extraPersons.length,
        rbservice_id: r.bookings[0].rbservice_id,
        service: this.services.find((s) => s.id == r.bookings[0].rbservice_id),
        reservation: r.key,
        slots: r.bookings.map((b) => ({
          start: b.start,
          end: b.end,
          staff_id: b.staff_id,
          rbservice_id: b.rbservice_id,
          site_id: b.site_id,
          optional_resources: b.optional_resources,
          count: b.count,
        })),
        extra_person: this.extraPersons,
        context: {
          service: this.services.find(
            (s) => s.id == r.bookings[0].rbservice_id
          ),
          start: Date.newFull(r.bookings[0].start),
          staff: this.staffs.find((s) => s.id == r.bookings[0].staff_id),
          period: this.getPeriod(r.bookings[0].start),
        },
        extended_info: Object.fromEntries(
          Object.values(r.questions).map((q) => [q.name, q.value])
        ),
        tmp_id: this.bookingId + "-" + (index + 1),
        site_id: r.bookings[0].site_id,
      }));

      this.reservations.forEach((r, index) => {
        this.additionalProducts[r.key].forEach((product) => {
          let b = r.basket[product.id];
          b.forEach((bi) => {
            if (bi.count) {
              items.push({
                count: bi.count,
                rbooking_id: this.bookingId + "-" + (index + 1),
                price: product.price,
                product_id: product.id,
                variant: bi.variant,
              });
            }
          });
        });
      });

      return items;
    },
    isReserving() {
      return !!this.reservations.find(
        (r) => !!r.bookings.find((b) => b.reserving)
      );
    },
    showPrice() {
      return !!this.$store.state.settings?.modules?.Payment;
    },
    productsInBasket() {
      return mergeBaskets(this.reservations);
    },
    contactinfo() {
      if (!this.$store.state.settings) {
        return {};
      }

      if (this.$store.state.settings?.sites?.length > 1) {
        let site = this.$store.state.settings.sites.find(
          (x) => x.id == this.siteId
        );
        return {
          visit_address: site?.address,
          visit_zipCode: site?.zipCode,
          visit_city: site?.city,
        };
      } else {
        return this.$store.state.settings.contactinfo;
      }
    },
    steps() {
      if (this.reservations.length == 0) {
        return [];
      }
      let has_questions = this.reservations.find(
        (r) =>
          this.services.find((s) => s.id == r.bookings[0].rbservice_id)
            .extendedInfo.length
      );
      let has_additional_products = this.reservations.find(
        (r) =>
          this.services.find((s) => s.id == r.bookings[0].rbservice_id)
            .additional_products?.length
      );

      let optional_resources_steps = [];
      this.reservations.forEach((r) => {
        const service = this.services.find((s) => s.id == r.bookings[0].rbservice_id);
        if (!service) return;

        if (service.resources?.length && !this.$store.state.settings?.modules?.WebShop) {
          // staff_id + date (YYYY-MM-DD)
          const groups = new Map(); // key: `${staff_id}|${date}` -> bookings[]

          r.bookings.forEach((b) => {
            const date = new Date(b.start).yyyymmdd();
            const key = `${b.staff_id}|${date}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(b);
          });

          for (const [key, bookings] of groups.entries()) {
            const staffId = Number(key.split("|")[0]);
            const staff = this.staffs.find((s) => s.id == staffId);
            const name  = staff ? staff.name : this.storedStaffNames[staffId] || this.$translate("Resource");
            const title = `${name}`;

            const intersection = (() => {
              if (bookings.length === 0) return [];

              // Start with the optional resources of the first booking
              const commonResources = new Set(bookings[0].optional_resources || []);

              // Compare against the rest of the bookings
              for (let i = 1; i < bookings.length; i++) {
                const currentResources = new Set(bookings[i].optional_resources || []);
                for (const resource of commonResources) {
                  if (!currentResources.has(resource)) {
                    commonResources.delete(resource);
                  }
                }
              }

              return [...commonResources];
            })();

            const sortedStarts = bookings.map(b => b.start).sort();
            const sortedEnds   = bookings.map(b => b.end).sort();
            const groupStart   = sortedStarts[0];
            const groupEnd     = sortedEnds[sortedEnds.length - 1];

            optional_resources_steps.push({
              title,
              id: "optional_resources",
              context: {
                resource_name: name,
                start: groupStart,
                end: groupEnd,
                rbservice_id: bookings[0].rbservice_id,
                service,
                resource_ids: service.resources.map(rr => rr.id),
                reservation: r,
                groupBookings: Array.isArray(bookings) ? bookings : [],                 
                selected_resource_ids: intersection,
              },
              complete: true,
            });
          }
        }
      });
      const pay = this.$translate("Pay");

      let payButtonTitle =
        this.cost > 0
          ? `${pay} ${formatPrice(this.cost)}`
          : this.$translate("Confirm booking");
      let payTitle = this.cost > 0 ? pay : this.$translate("Confirm booking");
      let showExtraPersons = this.selectedSlotHasCapacity;
      let personsHaveName = this.extraPersons.every((p) => p.name);
      let cartDisabled = !this.readyToPayExceptPaymentMethod;
      let extraButton = true && {
        label: this.$translate("Add to cart and continue shopping"),
        clicked: this.addToCart,
        disabled: cartDisabled,
      };

      return optional_resources_steps
        .concat([
          showExtraPersons && {
            title: this.$translate("Book more"),
            id: "extra_person",
            complete: personsHaveName,
          },
          has_questions && {
            title: this.$translate("Questions"),
            id: "questions",
            complete: this.questionsDone,
          },
          has_additional_products && {
            title: this.$translate("Optional"),
            id: "products",
            complete: true,
          },
          {
            title: payTitle,
            id: "checkout",
            nextTitle: payButtonTitle,
            nextColor: "#7bbd1b",
            nextDark: true,
            nextClicked: this.pay,
            nextLoading: this.checkoutLoading,
            complete: this.readyToPay && this.$store.state.user,
            extraButton,
          },
          { title: this.$translate("Done"), id: "done" },
        ])
        .filter((x) => x);
    },
  },
  methods: {
    optionalResourceSelectedGroup(reservation, groupBookings, resource_id, selected) {
      for (const b of groupBookings) {
        const i = b.optional_resources.indexOf(resource_id);
        if (selected) {
          if (i === -1) b.optional_resources.push(resource_id);
        } else {
          if (i !== -1) b.optional_resources.splice(i, 1);
        }
      }
      this.updateReservation(reservation);
    },
    isSlotSelected(start, resourceId) {
      return this.selectedSlotKeys.has(`${start}|${resourceId}`);
    },
    buildBookingObj(resource, slot) {
      return {
        start: slot.start,
        end: slot.end,
        rbservice_id: resource.serviceId,
        staff_id: resource.resourceId,
        site_id: resource.sites[0],
        optional_resources: [],
        count: 1,
      };
    },
    async getFreeSlots() {
      if (this.selectedService != null && this.selectedCategory != null) {
        // Fetches all available resources for the selected day and adds all available timeslots
        let start = this.selectedDate;
        let end = this.selectedDate;

        let slots = await this.$api.get("/api/public/resourcebooking/get", {
          service: this.selectedService.id,
          start: start,
          end: end,
          site: this.globalSiteSetting
            ? this.$store.state.selectedSiteId
            : null,
        });

        let timeslots = {};

        for (let resource of this.selectedService.staffs) {
          timeslots[resource.id] = {
            serviceId: this.selectedService.id,
            sites: resource.sites,
            resourceId: resource.id,
            title: resource.firstname
              ? resource.firstname + " " + resource.lastname
              : resource.lastname,
            slots: [],
            description: resource.description,
            imagekey: resource.imagekey,
            xstaffs: this.selectedService.xstaffs,
            tags: resource.tags || []
          };
        }

        // Populating the slots from the api
        for (let slot of slots) {
          for (let resourceId of slot["staffs"]) {
            let slot_entry = {
              start: slot["start"],
              end: slot["end"],
            };

            if (
              timeslots[resourceId] &&
              !timeslots[resourceId].slots.some(
                (s) => s.start === slot_entry.start && s.end === slot_entry.end
              )
            ) {
              timeslots[resourceId].slots.push(slot_entry);
            }
          }
        }

        // Include the users reserved slots in the results,
        for (const reservation of this.reservations) {
          for (const booking of reservation.bookings) {
            const bookingDate = booking.start.split(" ")[0];
            if (
              booking.rbservice_id === this.selectedService.id &&
              bookingDate === this.selectedDate
            ) {
              const resourceId = booking.staff_id;
              const slot_entry = {
                start: booking.start,
                end: booking.end,
              };

              if (timeslots[resourceId]) {
                if (
                  !timeslots[resourceId].slots.some(
                    (s) =>
                      s.start === slot_entry.start && s.end === slot_entry.end
                  )
                ) {
                  timeslots[resourceId].slots.push(slot_entry);
                  timeslots[resourceId].slots.sort(
                    (a, b) => new Date(a.start) - new Date(b.start)
                  );
                }
              }
            }
          }
        }

        this.resourceSlots = timeslots;
      } else {
        this.resourceSlots = {};
      }
    },
    async getServices(categoryId) {
      this.services = await this.$api.get(
        "/api/public/resourcebooking/service/get",
        {
          category: categoryId,
          site: this.globalSiteSetting
            ? this.$store.state.selectedSiteId
            : null,
        }
      );
    },

    setServices(serviceIds) {
      this.selectedService = serviceIds.concat();
    },

    incrementDate() {
      this.selectedDate = Date.newFull(this.selectedDate).addDays(1).yyyymmdd();
    },
    decrementDate() {
      this.selectedDate = Date.newFull(this.selectedDate).addDays(-1).yyyymmdd();
    },
    dateChanged(newDate) {
      this.selectedDate = newDate;
      this.showDatePicker = false;
    },
    getPeriod(time) {
      let startHour = Date.newFull(time).getHours();
      return DAY_PERIODS.find(
        (date) => startHour >= date.start && startHour < date.end
      );
    },
    setItem(list, id, selected) {
      let i = list.indexOf(id);
      if (selected && i == -1) {
        list.push(id);
      } else if (!selected && i != -1) {
        list.splice(i, 1);
      }
    },
    toggleSlot(slot) {
      if (!this.isSlotSelected(slot.start, slot.resourceId)) {
        this.reserveSlot(slot);
      } else {
        this.unreserveSlot(slot);
      }
    },
    async deselectSlot(slot) {
      await this.unreserveSlot(slot);
    },
    hasSlotForStart(resource, start) {
      return resource.slots.some((slot) => slot.start === start);
    },
    handleSlotClick(resource, start) {
      const slot = resource.slots.find((slot) => slot.start === start);
      if (!slot) return;
      this.toggleSlot(
        {
          start: start,
          end: slot.end,
          resourceId: resource.resourceId,
          serviceId: resource.serviceId,
          sites: resource.sites,
          xstaffs: resource.xstaffs,
        },
        resource.title
      );
    },
    getSlotColor(resource, start) {
      return this.isSlotSelected(start, resource.resourceId) ? 'success' : '';
    },
    handleColumnClick(start) {
      if (!this.isColumnSelected(start)) {
        this.selectSlotColumn(start);
      } else {
        this.deselectSlotColumn(start);
      }
    },
    getColumnButtonText(start) {
      return !this.isColumnSelected(start)
        ? this.$translate('Select all')
        : this.$translate('Remove all');
    },
    getColumnButtonIcon(start) {
      return !this.isColumnSelected(start) ? "mdi-arrow-down" : "mdi-close";
    },
    getQuestionPanelTitle(reservation) {
      const service = this.services.find(
        (s) => s.id == reservation.bookings[0].rbservice_id
      );
      return this.$translate("Supplementary questions for {0}").format(
        service ? service.name : ""
      );
    },
    optionalResourceSelected(reservation, booking, resource_id, selected) {
      let i = booking.optional_resources.indexOf(resource_id);
      if (!selected) {
        if (i != -1) {
          booking.optional_resources.splice(i, 1);
        }
      } else {
        if (i == -1) {
          booking.optional_resources.push(resource_id);
        }
      }

      this.updateReservation(reservation);
    },
    addExtraPerson() {
      this.extraPersons.push({
        name: this.$translate("Participant {0}").format(
          this.extraPersons.length + 2
        ),
      });
      this.reservations.forEach((reservation) => {
        this.$set(reservation, "count", this.extraPersons.length + 1);
        this.updateReservation(reservation);
      });
    },
    removeExtraPerson(index) {
      this.extraPersons.splice(index, 1);
      this.reservations.forEach((reservation) => {
        this.$set(reservation, "count", this.extraPersons.length + 1);
        this.updateReservation(reservation);
      });
    },
    async checkAvailabilityForReservedSlots() {
      let bookings = this.reservations
        .map((r) =>
          r.bookings.map((b) => {
            let service = this.services.find((s) => s.id == b.rbservice_id);
            let resource_ids = service?.xstaffs?.map((x) => x.id) || [];
            resource_ids.push(b.staff_id);
            return {
              resource_ids: resource_ids,
              start: b.start,
              end: b.end,
              rbservice_id: b.rbservice_id,
              staff_id: b.staff_id,
            };
          })
        )
        .flat();

      if (bookings.length == 0) {
        this.seatsLeft = null;
        return;
      }

      this.checkingAvailability = true;

      try {
        const res = await this.$api.post('/api/resource/availability/slots/get', { bookings, mode: 'full' });
        if (!res.ok) throw new Error(`availability ${res.status}`);

        const data = await res.json();
        const availability = Object.values(data?.availability || {});
        const capacity   = Object.values(data?.capacity || {});
        if (!availability.length || !capacity.length) { this.seatsLeft = null; this.selectedSlotHasCapacity ??= false; return; }

        this.seatsLeft = availability.reduce((a,b) => Math.min(a,b), Infinity);
        if (this.selectedSlotHasCapacity === null) {
          const minCap = capacity.reduce((a,b) => Math.min(a,b), Infinity);
          this.selectedSlotHasCapacity = minCap > 1;
        }
      } catch (e) {
        Sentry.captureException(e);
        this.seatsLeft = null;
        this.selectedSlotHasCapacity ??= false;
      } finally {
        this.checkingAvailability = false;
      }

    },
    async reserveSlot(slot) {
      let reservedSlot = {
        start: slot.start,
        end: slot.end,
        rbservice_id: slot.serviceId,
        staff_id: slot.resourceId,
        reserving: true,
        site_id: slot.sites[0],
        optional_resources: [],
        count: 1,
      };

      if (slot.xstaffs && slot.xstaffs.length) {
        reservedSlot.xstaff_id =
          slot.xstaffs[Math.floor(Math.random() * slot.xstaffs.length)];
      }
      let r = this.reservations.find((r) =>
        r.bookings.find(
          (b) =>
            b.rbservice_id == slot.serviceId &&
            b.site_id == reservedSlot.site_id
        )
      );
      if (!r) {
        r = {
          bookings: [],
          key: null,
          replace: true,
          basket: {},
        };
        this.reservations.push(r);
      }
      r.bookings.push(reservedSlot);
      const newlyAddedBooking = reservedSlot;

      const response = await this.$api.post(
        "/api/resource/reservation/add_resourcebooking",
        r
      );
      if (response.ok) {
        const x = await response.json();
        this.$set(r, "key", x.key);

        const targetBookingIndex = r.bookings.findIndex(
          (b) =>
            b.start === newlyAddedBooking.start &&
            b.staff_id === newlyAddedBooking.staff_id &&
            b.rbservice_id === newlyAddedBooking.rbservice_id
        );

        if (targetBookingIndex !== -1) {
          this.$set(r.bookings[targetBookingIndex], "reserving", false);
        }

        try {
          let ecommerceTrackingItem = {
            id: "r" + reservedSlot.rbservice_id,
            count: reservedSlot.count,
          };

          let service = this.services.find(
            (s) => s.id == reservedSlot.rbservice_id
          );
          if (service) {
            ecommerceTrackingItem.name = service.name;
            ecommerceTrackingItem.price = service.price;
            ecommerceTrackingItem.vat = service.vat;

            this.$ecommercetracking.addToCart(ecommerceTrackingItem);
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      } else {
        this.$store.commit("showErrorDialog", {
          title: this.$translate("Error during reservation"),
          text: this.$translate(
            "Something went wrong when the booking was to be reserved"
          ),
        });
      }
    },
    async unreserveSlot(slot) {
      let r = this.reservations.find((r) =>
        r.bookings.find((b) => b.rbservice_id == slot.serviceId)
      );
      if (r) {
        let b = r.bookings.find(
          (b) => b.start == slot.start && b.staff_id == slot.resourceId
        );
        if (b) {
          b.reserving = true;
          if (r.bookings.length == 1) {
            this.$api
              .post("/api/resource/reservation/cancel", { key: r.key })
              .then(() => {
                b.reserving = false;
                let idx = this.reservations.indexOf(r);
                if (idx != -1) {
                  this.reservations.splice(idx, 1);
                }
              });
          } else {
            const bookingToRemove = b;
            const response = await this.$api.post(
              "/api/resource/reservation/add_resourcebooking",
              {
                ...r,
                bookings: r.bookings.filter((i) => i != b),
              }
            );

            if (response.ok) {
              const x = await response.json();
              this.$delete(r.bookings, r.bookings.indexOf(b));
              this.$set(r, "key", x.key);
            } else {
              const targetBookingIndex = r.bookings.findIndex(
                (b) =>
                  b.start === bookingToRemove.start &&
                  b.staff_id === bookingToRemove.staff_id &&
                  b.rbservice_id === bookingToRemove.rbservice_id
              );

              if (targetBookingIndex !== -1) {
                this.$set(r.bookings[targetBookingIndex], "reserving", false);
              }

              this.$store.commit("showErrorDialog", {
                title: this.$translate("Error during reservation"),
                text: this.$translate(
                  "Something went wrong when the booking was to be reserved"
                ),
              });
            }
          }
        }
      }
    },
    unreserveAll(wait) {
      let keys = this.reservations.map((r) => r.key);

      if (this.checkoutLoading) {
        return;
      }

      if (keys.length) {
        this.$api
          .post("/api/resource/reservation/cancel", { key: keys })
          .then(() => {
            let unreserve = () => {
              keys.forEach((key) => {
                let idx = this.reservations.findIndex((r) => r.key == key);
                if (idx != -1) {
                  this.reservations.splice(idx, 1);
                }
              });
            };

            if (wait) {
              let unwatch = this.$watch("resourceSlots", unreserve);

              setTimeout(() => {
                if (unwatch) {
                  unwatch();
                } else {
                  unreserve();
                }
              }, 3000);
            } else {
              unreserve();
            }
          });
      }
    },
    updateReservation(r) {
      r.bookings.forEach((b) => this.$set(b, "reserving", true));
      this.$api
        .post("/api/resource/reservation/add_resourcebooking", r)
        .then((response) => {
          if (response.ok) {
            response.json().then(() => {
              r.bookings.forEach((b) => this.$set(b, "reserving", false));
            });
          } else {
            this.$store.commit("showErrorDialog", {
              title: this.$translate("Error during reservation"),
              text: this.$translate(
                "Something went wrong when the booking was to be reserved"
              ),
            });
          }
        })
        .catch(() => {
          r.bookings.forEach((b) => this.$set(b, "reserving", false));
        });
    },
    addToCart() {
      const props = [
        "count",
        "rbservice_id",
        "reservation",
        "slots",
        "extra_person",
        "extended_info",
        "tmp_id",
        "site_id",
        "rbooking_id",
        "product_id",
        "variant",
        "price",
        "service",
      ];

      const cartName = "resursbokning";
      this.checkoutItems.forEach((item) => {
        let basketItem = {};
        props.forEach((prop) => {
          if (item[prop] !== undefined) {
            basketItem[prop] = item[prop];
          }
        });
        this.$store.dispatch("addToCart", basketItem);
      });
      this.$store.dispatch("setCartVisibility", {
        cartName,
        showBottomBar: true,
        showCheckout: false,
      });
      this.reservations = [];
      this.showCheckout = false;
    },
    changeNumberOfItems(item, count) {
      if (item.rbooking_id) {
        let index = Number(item.rbooking_id.split("-")[1]) - 1;
        let r = this.reservations[index];
        let b = r.basket[item.product_id];
        let bi = b?.find((bi) => bi.variant === item.variant);
        if (bi) {
          this.$set(bi, "count", count);
        }
      }
    },
    toggleResource(id) {
      if (this.selectedResourceIds.includes(id)) {
        this.selectedResourceIds = this.selectedResourceIds.filter(
          (slotId) => slotId !== id
        );
      } else {
        this.selectedResourceIds.push(id);
      }
    },
    toggleDatePicker() {
      this.showDatePicker = !this.showDatePicker;
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    handleOutsideClick(e) {
      const datepicker = this.$refs.datepicker?.$el;
      const button =
        this.$refs.datepickerButton?.$el || this.$refs.datepickerButton;

      if (
        datepicker &&
        button &&
        !datepicker.contains(e.target) &&
        !button.contains(e.target)
      ) {
        this.showDatePicker = false;
      }
    },
    async removeBooking(date, startTime, endTime, staffIdToRemove) {
      let bookingToUnreserve = null;
      // Iterate through reservations to find the specific booking
      for (const reservation of this.reservations) {
        for (const booking of reservation.bookings) {
          const bookingDate = booking.start.split(" ")[0];
          const bookingStartTime = booking.start.split(" ")[1];
          const bookingEndTime = booking.end.split(" ")[1];

          if (
            bookingDate === date &&
            bookingStartTime === startTime &&
            bookingEndTime === endTime &&
            booking.staff_id === staffIdToRemove
          ) {
            bookingToUnreserve = booking;
            break;
          }
        }
        if (bookingToUnreserve) break;
      }

      if (bookingToUnreserve) {
        const serviceForBooking = this.services.find(
          (s) => s.id === bookingToUnreserve.rbservice_id
        );
        const slotToUnreserve = {
          start: bookingToUnreserve.start,
          end: bookingToUnreserve.end,
          serviceId: bookingToUnreserve.rbservice_id,
          resourceId: bookingToUnreserve.staff_id,
          sites: [bookingToUnreserve.site_id],
          xstaffs: serviceForBooking ? serviceForBooking.xstaffs : [],
        };

        await this.unreserveSlot(slotToUnreserve);
      }
    },
    async selectSlotColumn(start) {
      this.bulkSelecting = true;
      this.suppressRealtime = true;

      const additions = [];
      for (const resource of this.filteredResources) {
        const slot = resource.slots.find(s => s.start === start);
        if (slot && !this.isSlotSelected(start, resource.resourceId)) {
          additions.push({ resource, slot });
        }
      }
      if (!additions.length) { this.bulkSelecting = false; this.suppressRealtime = false; return; }

      const groups = new Map(); // key: `${serviceId}|${siteId}` -> { serviceId, siteId, toAdd: [] }
      for (const { resource, slot } of additions) {
        const key = `${resource.serviceId}|${resource.sites[0]}`;
        if (!groups.has(key)) groups.set(key, { serviceId: resource.serviceId, siteId: resource.sites[0], toAdd: [] });
        groups.get(key).toAdd.push(this.buildBookingObj(resource, slot));
      }

      try {
        await Promise.all(Array.from(groups.values()).map(async (g) => {
          let r = this.reservations.find(x =>
            x.bookings.length &&
            x.bookings[0].rbservice_id === g.serviceId &&
            x.bookings[0].site_id === g.siteId
          );
          const existing = r ? r.bookings : [];
          const merged = existing.concat(g.toAdd.map(b => ({ ...b, reserving: true })));
          if (!r) {
            r = { bookings: [], key: null, replace: true, basket: {} };
            this.reservations.push(r);
          }
          this.$set(r, "bookings", merged);

          const resp = await this.$api.post("/api/resource/reservation/add_resourcebooking", r);
          if (!resp.ok) {
            this.$store.commit("showErrorDialog", {
              title: this.$translate("Error during reservation"),
              text: this.$translate("Something went wrong when the booking was to be reserved"),
            });
            return;
          }
          const x = await resp.json();
          this.$set(r, "key", x.key);
          r.bookings.forEach(b => this.$set(b, "reserving", false));

        }));
      } finally {
        this.bulkSelecting = false;
        this.suppressRealtime = false;
        this.getFreeSlots();
        this.checkAvailabilityForReservedSlots();
      }
    },
    async deselectSlotColumn(start) {
      this.bulkSelecting = true;
      this.suppressRealtime = true;

      try {
        const tasks = [];
        for (const r of [...this.reservations]) {
          const keep = r.bookings.filter(b => b.start !== start);
          if (keep.length === r.bookings.length) continue; 

          if (!keep.length) {
            tasks.push((async () => {
              await this.$api.post("/api/resource/reservation/cancel", { key: r.key });
              const idx = this.reservations.indexOf(r);
              if (idx !== -1) this.reservations.splice(idx, 1);
            })());
          } else {
            tasks.push((async () => {
              const payload = { ...r, bookings: keep.map(b => ({ ...b, reserving: true })) };
              const resp = await this.$api.post("/api/resource/reservation/add_resourcebooking", payload);
              if (!resp.ok) throw new Error("Reservation update failed");
              const x = await resp.json();
              this.$set(r, "key", x.key);
              this.$set(r, "bookings", keep.map(b => ({ ...b, reserving: false })));
            })());
          }
        }
        await Promise.all(tasks);
      } finally {
        this.bulkSelecting = false;
        this.suppressRealtime = false;
        this.getFreeSlots();
        this.checkAvailabilityForReservedSlots();
      }
    },
    checkoutDone() {
      this.reservations = [];
      this.reset();
    },
    reset() {
      this.unreserveAll(false);
      this.showCheckout = false;
      this.almostdone = false;
      this.currentStep = 0;
      this.extraPersons = [];
      this.selectedSlotHasCapacity = null;
      this.bookingId = getRandomId(8);
      this.getFreeSlots();
    },
    pay() {
      if (this.$store.state.settings?.modules?.WebShop) {
        this.$refs.checkout.completePurchase();
      } else {
        this.bookWithoutWebShop();
      }
    },
    async bookWithoutWebShop() {
      this.checkoutLoading = true;

      for (let r of this.reservations) {
        let bookings = r.bookings.map((b) => ({
          start: b.start,
          end: b.end,
          staff: b.staff_id,
          service: b.rbservice_id,
          extendedInfo: Object.fromEntries(
            Object.values(r.questions).map((q) => [q.name, q.value])
          ),
        }));

        let optional_resource = r.bookings.map((b) => ({
          room_id: b.staff_id,
          resources: b.optional_resources,
        }));

        try {
          let response = await this.$api.post(
            "/api/memberapi/resourcebooking/add",
            {
              bookings,
              _extra_info: {
                reservation: [r.key],
                extra_person: this.extraPersons,
                _additional_products: null,
              },
              optional_resource,
            }
          );

          if (!response.ok) {
            throw new Error(this.$translate("Booking error"));
          }
        } catch (error) {
          this.$store.commit("showErrorDialog", {
            title: this.$translate("Booking error"),
            text: error.message,
          });
          this.checkoutLoading = false;
          return;
        }
      }

      this.reservations = [];
      this.reset();
      this.$store.commit("showCheckoutDoneDialog", {
        thankYouText: this.$translate("Booking completed"),
      });
      this.checkoutLoading = false;
    },
    reservationTimeout() {
      this.showCheckout = false;
      this.reservations = [];
      this.resetSelections();
    },
    toggleTagFilter(tagId) {
      const i = this.selectedTagIds.indexOf(tagId);
      if (i >= 0) {
        this.selectedTagIds.splice(i, 1);
      } else {
        this.selectedTagIds.push(tagId);
      }
    },
    resetSelections() {
      this.selectedResourceIds = [];
      this.selectedSlotColumns = [];
      this.selectedTagIds = [];
    },
    openDialog(resourceId) {
      var targetResource = this.resources.find(
        (resource) => resource.resourceId === resourceId
      );

      this.infoResourceTitle = targetResource.title;
      this.infoResourceDescription = targetResource.description;
      this.infoResourceImageKey = targetResource.imagekey;
      this.dialogStaffId = resourceId;

      this.isDialogShown = true;
    },
    formatTimeOnly(timeString) {
      return timeString.split(":").slice(0, 2).join(":");
    },
    onResize() {
      this.isMobile = window.innerWidth < 600;
    },
  },
  mounted() {
    this.onResize();
    window.addEventListener("resize", this.onResize, { passive: true });
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
    if (typeof window === "undefined") return;

    window.removeEventListener("resize", this.onResize, { passive: true });
  },
  created() {
    this.unsubscribe = this.$root.$api.subscribe({
      type: "resourceallocation",
      callback: () => {
        if (!this.suppressRealtime) {
          this.getFreeSlots();
          this.checkAvailabilityForReservedSlots();
        }
      },
    });
    window.addEventListener("beforeunload", this.unreserveAll);
  },
  destroyed() {
    this.unreserveAll(false);
    window.removeEventListener("beforeunload", this.unreserveAll);
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
};
</script>

<style scoped>
.slots-container {
  overflow: visible;
}

.slot-grid {
  gap: 8px;
  white-space: nowrap;
  padding: 8px 0 16px 0;
  overflow: auto;
}

.slot-row {
  gap: 8px;
  justify-content: stretch;
}

.timeslots {
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  gap: 8px;
  position: relative;
  justify-content: center;
  align-items: center;
  height: 50px;
}

.timeslot {
  width: 100px;
  height: 52px;
  display: flex;
}

.timeslot-row-select {
  color: #b0b6c2;
  font-size: 12px;
  text-align: center;
}

.selectable-divider {
  height: 2.3rem;
}

.available-slot:hover {
  cursor: pointer;
  background-color: #c5cbd5;
}

.timeslot p {
  padding: 0;
}

.available-slot {
  padding: 8px;
  background-color: #b0b6c2;
  border-radius: 4px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.selectedslot {
  background-color: #4caf50;
}

.selectedslot:hover {
  background-color: #5bc45e;
}

.select-all-slots {
  height: 20px;
}

.timeslot-row-select {
  color: #b0b6c2;
}

.timeslot-row-select:hover {
  cursor: pointer;
  color: #c5cbd5;
}

.non-selectable {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.top-right-btn {
  position: absolute;
  top: 8px;
  right: 8px;
}

.checkout-summary {
  width: 25%;
  position: sticky;
  right: 0;
  top: 120px;
  left: 0;
}

.sticky-column {
  position: sticky;
  top: 120px;
}

.resource-slot {
  width: 250px;
}

.resource-image-small {
  width: 75px;
  height: 52px;
}

.resource-title {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  max-height: 52px;
}

.resource-card-title {
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.OptionalResources {
  max-height: clamp(18rem, 60vh, 38rem);
  overflow: auto;
}

@media (max-height: 700px) {
  .OptionalResources {
    max-height: clamp(16rem, 55vh, 30rem);
  }
}

@media (max-width: 600px) {
  .checkout-summary {
    width: 100%;
  }

  .slot-grid {
    overflow: visible;
  }

  .slots-container {
    overflow: auto;
  }

  .timeslot {
    width: 150px;
  }

  .resource-slot {
    width: 150px !important;
    flex: 0 0 150px;
  }

  .resource-image-small {
    width: 150px;
    height: 75px;
  }
}

/* Compact cards */
.z-compact-resources {
  gap: 4px;
}

.z-compact-chip {
  max-width: 260px;
}

.z-compact-name {
  max-width: 160px;
  display: inline-block;
  vertical-align: middle;
}
@media (max-width: 600px) {
  .z-compact-chip { max-width: 100%; }
  .z-compact-name { max-width: 56vw; }
}
</style>
